const _ = require('lodash')
const viewLocals = require('client/lib/view-locals')
const Emitter = require('client/lib/emitter')

const BASE_CLASSNAME = 'base-modal'
const BASE_SELECTOR = '.' + BASE_CLASSNAME
const BACKDROP_SELECTOR = BASE_SELECTOR + '__backdrop'
const CLOSE_BUTTON_SELECTOR = BASE_SELECTOR + '__close-button'

const appSupportsEvents = typeof app === 'object' && typeof app.on === 'function'

module.exports = class BaseModal extends Emitter {
  constructor(options) {
    super()
    // Handle options
    if (!_.get(options, 'modalTemplate') || !_.isFunction(options.modalTemplate)) {
      throw Error('Required option `modalTemplate` must be a Function')
    }
    this.options = _.defaults(options, {
      modalTemplate: null, // Caller MUST provide a template
      modalTitle: '',
      modalClassName: '',
      modalBaseClassName: BASE_CLASSNAME,
      modalHasBackdrop: true,
      modalHasHeader: true,
      modalHasCloseButton: true,
      resolve: _.noop,
      reject: _.noop
    })
    // Allow opt-out/swap-in of the base modal class styles
    if (this.options.modalBaseClassName) {
      const classNames = this.options.modalClassName.split(' ')
      classNames.push(this.options.modalBaseClassName)
      this.options.modalClassName = classNames.join(' ')
    }
    this.viewLocals = _.clone(viewLocals)
    // Class method usage convenience
    _.bindAll(this, ['resolve', 'reject', 'dismiss'])
  }

  static present(options) {
    return (new this(options)).attach()
  }

  static promise(options) {
    let modal
    const promise = new Promise((resolve, reject) => {
      modal = this.present(_.defaults({ resolve, reject }, options))
    })
    return _.assign(promise, { modal })
  }

  resolve(...args) {
    this.detach()
    this.options.resolve(...args)
    _.merge(this.options, { resolve: _.noop, reject: _.noop })
  }

  reject(reason) {
    this.detach()
    this.options.reject(_.isError(reason) ? reason : new Error(reason))
    _.merge(this.options, { resolve: _.noop, reject: _.noop })
  }

  dismiss() {
    this.resolve(null)
  }

  render() {
    let templateOptions = _.assign({}, this.options, this.viewLocals)
    this.trigger('will-render')
    this.willRender(templateOptions)
    let html = this.options.modalTemplate(templateOptions)
    if (this.$el) {
      this.$el.html(html)
    } else {
      this.$el = $(html)
    }
    this.didRender()
    this.trigger('did-render')
    return this.$el
  }

  willRender() {}

  didRender() {
    this.registerDefaultModalEventListeners()
  }

  get isAttached() {
    return !!this.$el && $.contains(document.documentElement, this.$el[0])
  }

  attach() {
    if (!this.$el) {
      this.render()
    }
    if (!this.isAttached) {
      this.trigger('will-open')
      this.$el.appendTo(document.body)
      this.trigger('did-open')
      this.registerNavigationEventListeners()
      if (appSupportsEvents) {
        app.on('should-close-modal', this.dismiss)
      }
    }
    return this
  }

  detach() {
    if (this.isAttached) {
      this.trigger('will-close')
      this.$el.remove()
      this.trigger('did-close')
      this.unregisterNavigationEventListeners()
      if (appSupportsEvents) {
        app.off('should-close-modal', this.dismiss)
      }
    }
    return this
  }

  registerDefaultModalEventListeners() {
    this.$el.on('click', BACKDROP_SELECTOR, this.dismiss)
    this.$el.on('click', CLOSE_BUTTON_SELECTOR, this.dismiss)
  }

  registerNavigationEventListeners() {
    if (appSupportsEvents) {
      app.on('clicknavigate', this.dismiss)
    }
    window.addEventListener('popstate', this.dismiss)
  }

  unregisterNavigationEventListeners() {
    if (appSupportsEvents) {
      app.off('clicknavigate', this.dismiss)
    }
    window.removeEventListener('popstate', this.dismiss)
  }
}
